import React from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { formatIsoDate } from '../../schedule/lib/format'
import { CONTRACT_KIND_KEY, CONTRACT_TYPE_KEY } from '../lib/contracts'
import { formatMoney, isNegativeMoney } from '../lib/money'
import type { CostContract } from '../types'
import DetailRow from '../components/DetailRow'

const DASH = '—'

interface Props { contract: CostContract; projectId: string; currency: string; locale: string; canOpenTender: boolean }

const ContractSummaryCards: React.FC<Props> = ({ contract, projectId, currency, locale, canOpenTender }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  const date = (v: string | null) => (v ? formatIsoDate(t, v) : DASH)
  const period = contract.startDate || contract.endDate ? `${date(contract.startDate)} – ${date(contract.endDate)}` : DASH
  const { totals } = contract
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{t('CostControl_contract_details_title')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y">
            <DetailRow label={t('CostControl_contract_field_counterparty')}>
              {contract.counterpartyName}
              {contract.counterpartyIco && <span className="text-muted-foreground"> · {t('CostControl_contract_field_ico')} {contract.counterpartyIco}</span>}
            </DetailRow>
            <DetailRow label={t('CostControl_contract_field_kind')}>{t(CONTRACT_KIND_KEY[contract.kind])}</DetailRow>
            <DetailRow label={t('CostControl_contract_field_type')}>{t(CONTRACT_TYPE_KEY[contract.contractType])}</DetailRow>
            <DetailRow label={t('CostControl_contract_field_number')}>{contract.number || DASH}</DetailRow>
            <DetailRow label={t('CostControl_contract_field_signedAt')}>{date(contract.signedAt)}</DetailRow>
            <DetailRow label={t('CostControl_contract_field_period')}>{period}</DetailRow>
            {contract.tenderId && (
              <DetailRow label={t('CostControl_contract_field_tender')}>
                {canOpenTender ? (
                  <Link className="text-primary hover:underline" to={`/project/${projectId}/tendering/tenders/${contract.tenderId}`}>{contract.tenderName}</Link>
                ) : contract.tenderName}
              </DetailRow>
            )}
            {contract.rfqId && (
              <DetailRow label={t('CostControl_rfq_source')}>
                <Link className="text-primary hover:underline" to={`/project/${projectId}/cost-control/rfqs/${contract.rfqId}`}>{t('CostControl_rfq_from', { number: contract.rfqNumber })}</Link>
              </DetailRow>
            )}
            <DetailRow label={t('CostControl_contract_field_notes')}><span className="whitespace-pre-wrap">{contract.notes || DASH}</span></DetailRow>
          </dl>
        </CardContent>
      </Card>
      <Card className="border-0 shadow-sm">
        <CardHeader><CardTitle className="text-base">{t('CostControl_contract_value_title')}</CardTitle></CardHeader>
        <CardContent>
          <dl className="divide-y">
            <DetailRow label={t(contract.kind === 'MAIN' ? 'CostControl_contract_field_mainValue' : 'CostControl_contract_value_lines')}>
              <span className="tabular-nums">{contract.kind === 'MAIN' && !contract.mainValue ? DASH : money(totals.value)}</span>
            </DetailRow>
            {contract.kind === 'MAIN' && contract.investorValue != null && (
              <DetailRow label={t('CostControl_contract_field_investorValue')}><span className="tabular-nums">{money(contract.investorValue)}</span></DetailRow>
            )}
            {contract.kind === 'SUB' && (
              <>
                <DetailRow label={t('CostControl_contract_approved_changes')}><span className="tabular-nums">{money(totals.approvedChanges)}</span></DetailRow>
                <DetailRow label={t('CostControl_contract_current_value')}><strong className="tabular-nums">{money(totals.currentValue)}</strong></DetailRow>
                <DetailRow label={t('CostControl_contract_pending_changes')}><span className="tabular-nums">{money(totals.pendingChanges)}</span></DetailRow>
                <DetailRow label={t('CostControl_contract_invoiced')}><span className="tabular-nums">{money(totals.invoiced)}</span></DetailRow>
                <DetailRow label={t('CostControl_contract_remaining')}>
                  <span className={cn('tabular-nums', isNegativeMoney(totals.remaining) && 'text-destructive')}>{money(totals.remaining)}</span>
                </DetailRow>
              </>
            )}
          </dl>
          {contract.kind === 'SUB' && contract.status === 'DRAFT' && <p className="mt-3 text-xs text-muted-foreground">{t('CostControl_contract_draft_hint')}</p>}
        </CardContent>
      </Card>
    </div>
  )
}
export default ContractSummaryCards
